import { useEffect, useId, useRef, useState } from 'react'

import { FOCUS } from '@/design-system/ui'

// The SeaFi product suite — same source of truth as the brand landing's launch menu.
const TELEGRAM_URL = 'https://t.me/DanielTKilleen'

type Product = { category: string; href: string; logo: string; name: string }

const PRODUCTS: Product[] = [
  {
    category: 'Recovery',
    href: 'https://exitsooner.com',
    logo: '/assets/img/products/sooner.svg',
    name: 'Sooner',
  },
  {
    category: 'Yield',
    href: 'https://vaults.seafi.app',
    logo: '/assets/img/products/avax.svg',
    name: 'Vaults',
  },
  {
    category: 'Infrastructure',
    href: 'https://baseazul.dev',
    logo: '/assets/img/products/azul.svg',
    name: 'Base Azul',
  },
  {
    category: 'Marketplace',
    href: 'https://eulerdebt.com',
    logo: '/assets/img/products/euler.svg',
    name: 'Euler Debt',
  },
  {
    category: 'Yield',
    href: 'https://seafi.app/treasury',
    logo: '/assets/img/products/treasury.svg',
    name: 'Treasury',
  },
]

function ArrowUpRight() {
  return (
    <svg
      aria-hidden="true"
      className="pl-arrow"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="16"
    >
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}

function TelegramIcon() {
  return (
    <svg aria-hidden="true" fill="currentColor" height="16" viewBox="0 0 24 24" width="16">
      <path d="M21.94 4.3 2.66 11.7c-.9.35-.9 1.58.01 1.92l4.9 1.63 1.87 5.86c.23.7 1.1.92 1.63.36l2.73-2.62 4.78 3.5c.6.44 1.44.11 1.6-.6l3.19-15.3c.19-.88-.68-1.65-1.44-1.35Z" />
    </svg>
  )
}

// A 2×2 app-switcher grid — the product suite mark. Matches the other bottom-nav icons exactly
// (22px box, 1.7 stroke) so every tab's icon and label line up.
function GridIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.7"
      viewBox="0 0 24 24"
      width="24"
    >
      <rect height="7" rx="1.6" width="7" x="4" y="4" />
      <rect height="7" rx="1.6" width="7" x="13" y="4" />
      <rect height="7" rx="1.6" width="7" x="4" y="13" />
      <rect height="7" rx="1.6" width="7" x="13" y="13" />
    </svg>
  )
}

// SeaFi product launcher: opens a dark navy panel listing every SeaFi app plus a contact row.
// `variant="header"` renders a nav segment and opens downward; `variant="dock"` renders a
// bottom-nav tab and opens upward. The panel is fixed-positioned from the trigger's rect so it
// escapes the header pill's (and bottom pill's) overflow clip.
export function ProductLauncher({ variant = 'header' }: { variant?: 'dock' | 'header' }) {
  const [open, setOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        btnRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Fixed-position the panel from the trigger's rect (escapes the pill overflow clip). The dock
  // variant anchors to the bottom and spans the mobile gutter; the header variant drops down.
  useEffect(() => {
    if (!open) return
    function place() {
      const r = btnRef.current?.getBoundingClientRect()
      if (!r) return
      if (variant === 'dock') {
        const gutter = window.innerWidth <= 480 ? 16 : 24
        setPanelStyle({
          bottom: window.innerHeight - r.top + 8,
          left: gutter,
          position: 'fixed',
          right: gutter,
        })
      } else {
        setPanelStyle({
          position: 'fixed',
          right: Math.max(8, window.innerWidth - r.right),
          top: r.bottom + 8,
        })
      }
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, variant])

  const isDock = variant === 'dock'

  return (
    <div
      ref={rootRef}
      style={
        isDock ? { display: 'flex', flex: 1, minWidth: 0 } : { flex: 'none', position: 'relative' }
      }
    >
      {isDock ? (
        <button
          aria-controls={menuId}
          aria-expanded={open}
          aria-haspopup="menu"
          className={`app-bottomnav-item ${open ? 'is-active' : ''} ${FOCUS}`}
          onClick={() => setOpen((v) => !v)}
          ref={btnRef}
          style={{
            background: open ? 'var(--ink)' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            width: '100%',
          }}
          type="button"
        >
          <GridIcon />
          <span>SeaFi</span>
        </button>
      ) : (
        <button
          aria-controls={menuId}
          aria-expanded={open}
          aria-haspopup="menu"
          className={`app-seg ${open ? 'is-active' : ''} ${FOCUS}`}
          onClick={() => setOpen((v) => !v)}
          ref={btnRef}
          style={{ border: 'none', cursor: 'pointer' }}
          type="button"
        >
          SeaFi
          <svg
            aria-hidden="true"
            fill="none"
            height="14"
            style={{
              marginLeft: 6,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s ease',
            }}
            viewBox="0 0 16 16"
            width="14"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.6"
            />
          </svg>
        </button>
      )}

      {open && panelStyle && (
        <div
          className={`pl-panel ${isDock ? 'pl-panel--dock' : ''}`}
          id={menuId}
          role="menu"
          style={panelStyle}
        >
          <p className="pl-grouplabel">Products</p>
          {PRODUCTS.map((product) => (
            <a
              className="pl-item"
              href={product.href}
              key={product.name}
              rel="noopener noreferrer"
              role="menuitem"
              target="_blank"
            >
              <span className="pl-tile">
                <span
                  aria-hidden="true"
                  className="pl-mark"
                  style={{
                    maskImage: `url(${product.logo})`,
                    WebkitMaskImage: `url(${product.logo})`,
                  }}
                />
              </span>
              <span className="pl-text">
                <span className="pl-name">{product.name}</span>
                <span className="pl-cat">{product.category}</span>
              </span>
              <ArrowUpRight />
            </a>
          ))}

          <div className="pl-divider" role="separator" />

          <a
            className="pl-contact"
            href={TELEGRAM_URL}
            rel="noopener noreferrer"
            role="menuitem"
            target="_blank"
          >
            <TelegramIcon />
            Get in touch
          </a>
        </div>
      )}
    </div>
  )
}
